import React, { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { LANGUAGES, DEFAULT_LANGUAGE, type LanguageConfig } from "./languages";
import { translations, type TranslationKey } from "./translations";

interface I18nContextType {
    language: string;
    setLanguage: (code: string) => void;
    t: (key: TranslationKey) => string;
    languages: LanguageConfig[];
    currentLanguage: LanguageConfig | undefined;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const STORAGE_KEY = "cyber_web_language";

interface I18nProviderProps {
    children: ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
    const [language, setLanguageState] = useState<string>(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored && LANGUAGES.find((l) => l.code === stored)) {
            return stored;
        }
        const browserLang = navigator.language.split("-")[0].toLowerCase();
        if (LANGUAGES.find((l) => l.code === browserLang)) {
            return browserLang;
        }
        return DEFAULT_LANGUAGE;
    });

    const setLanguage = useCallback((code: string) => {
        if (LANGUAGES.find((l) => l.code === code)) {
            setLanguageState(code);
            localStorage.setItem(STORAGE_KEY, code);
            document.documentElement.lang = code;
        }
    }, []);

    const t = useCallback(
        (key: TranslationKey): string => {
            if (!key) return key;
            const lang = language as keyof typeof translations;
            const langTranslations = translations[lang];
            if (!langTranslations) return (translations.pl as Record<string, string>)[key] || key;
            return (langTranslations as Record<string, string>)[key] || (translations.pl as Record<string, string>)[key] || key;
        },
        [language]
    );

    const currentLanguage = LANGUAGES.find((l) => l.code === language);

    return (
        <I18nContext.Provider value={{ language, setLanguage, t, languages: LANGUAGES, currentLanguage }}>
            {children}
        </I18nContext.Provider>
    );
};

export const useI18n = (): I18nContextType => {
    const context = useContext(I18nContext);
    if (!context) {
        throw new Error("useI18n must be used within an I18nProvider");
    }
    return context;
};
